import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Search, Mail, Phone, Paperclip, Check, RotateCcw, FileDown, Loader2, MailWarning, CheckCircle2, AlertTriangle, Clock } from 'lucide-react';
import { log } from '../../lib/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_BADGE = {
  new: 'bg-amber-100 text-amber-800 ring-1 ring-amber-300',
  quoted: 'bg-green-100 text-green-800 ring-1 ring-green-300',
};

/**
 * Small icon-only chip that surfaces whether the admin notification
 * email actually went out for a quote request. Hover for full details
 * (last error, send timestamp, retry count). Hidden entirely when the
 * record predates email-delivery tracking (no email_status field).
 */
const EmailStatusChip = ({ row }) => {
  if (!row.email_status) return null;
  const meta = {
    sent:    { Icon: CheckCircle2,  cls: 'text-emerald-600',   label: 'Email delivered' },
    failed:  { Icon: AlertTriangle, cls: 'text-red-600',       label: 'Email failed' },
    pending: { Icon: Clock,         cls: 'text-amber-600',     label: 'Email queued' },
  }[row.email_status] || { Icon: Clock, cls: 'text-amber-600', label: row.email_status };
  const { Icon, cls, label } = meta;
  const tip = (() => {
    if (row.email_status === 'failed') {
      return `${label}${row.email_error ? ` — ${row.email_error}` : ''}`;
    }
    if (row.email_status === 'sent' && row.email_sent_at) {
      return `${label} — ${new Date(row.email_sent_at).toLocaleString()}${(row.email_attempts || 1) > 1 ? ` (${row.email_attempts} attempts)` : ''}`;
    }
    return label;
  })();
  return (
    <span
      title={tip}
      data-testid={`quote-email-chip-${row.id}`}
      data-email-status={row.email_status}
      className={`inline-flex items-center ${cls}`}
    >
      <Icon className="w-3.5 h-3.5" />
    </span>
  );
};

const formatWhen = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch { return iso; }
};

/**
 * Admin-only table for `quote_requests` (submissions from the site's
 * Custom Quote modal). Provides:
 *   • case-insensitive search across email / product / name
 *   • new ↔ quoted status filter + inline toggle
 *   • attachment download per row
 *   • a soft warning badge when attachments were GC'd off disk so the
 *     admin knows to email the customer back to re-request files
 */
export const QuoteRequestsTable = ({ onStatusChange }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [mutatingId, setMutatingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (statusFilter) params.status = statusFilter;
      const r = await axios.get(`${API_URL}/api/quote-request/admin/list`, { params });
      setItems(r.data?.items || []);
    } catch (e) {
      log.error('Quote request list failed:', e);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  // Debounce search input so the admin can type freely without the
  // list flashing on every keystroke.
  useEffect(() => {
    const t = setTimeout(fetchList, 300);
    return () => clearTimeout(t);
  }, [fetchList]);

  const toggleStatus = async (row) => {
    const target = row.status === 'quoted' ? 'new' : 'quoted';
    setMutatingId(row.id);
    try {
      const form = new FormData();
      form.append('new_status', target);
      await axios.patch(`${API_URL}/api/quote-request/admin/${row.id}/status`, form);
      setItems((prev) => prev.map((r) => (r.id === row.id ? { ...r, status: target } : r)));
      // Poke the dashboard-level count so the red pill on the tab
      // button updates the instant the admin marks a row.
      onStatusChange?.();
    } catch (e) {
      log.error('Quote status toggle failed:', e);
    } finally {
      setMutatingId(null);
    }
  };

  const downloadAttachment = async (id, index, name) => {
    try {
      const r = await axios.get(
        `${API_URL}/api/quote-request/admin/${id}/attachment/${index}`,
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = name || `attachment-${index}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      const code = e?.response?.status;
      if (code === 410) {
        // Use a sonner toast (matches the rest of the admin UX) so
        // multiple action results stack instead of forcing the admin
        // to OK-click through them one at a time.
        toast.warning('This attachment is no longer on disk.', {
          description: 'Ask the customer to re-send.',
        });
      } else {
        log.error('Attachment download failed:', e);
        toast.error('Could not download attachment.', {
          description: e?.response?.data?.detail || e?.message || 'Please try again.',
        });
      }
    }
  };

  const newCount = items.filter((r) => r.status === 'new').length;

  return (
    <div className="bg-white rounded-lg shadow-md" data-testid="quote-requests-table">
      <div className="p-6 border-b border-gray-200 flex flex-col md:flex-row md:items-center gap-3 md:justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Custom Quote Requests</h3>
          <p className="text-sm text-gray-500 mt-0.5">
            {items.length} total · <span className="text-amber-700 font-medium">{newCount} awaiting quote</span>
          </p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search email, product, name…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md w-64 focus:ring-2 focus:ring-[#4A7C59] focus:outline-none"
              data-testid="quote-requests-search"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-[#4A7C59] focus:outline-none bg-white"
            data-testid="quote-requests-status-filter"
          >
            <option value="">All status</option>
            <option value="new">New</option>
            <option value="quoted">Quoted</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="p-10 text-center text-gray-500 text-sm flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <div className="p-10 text-center text-gray-500 text-sm">
          No custom quote requests match your filters.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500">
              <tr>
                <th className="px-4 py-3 font-semibold">Submitted</th>
                <th className="px-4 py-3 font-semibold">Customer</th>
                <th className="px-4 py-3 font-semibold">Product</th>
                <th className="px-4 py-3 font-semibold">Qty / Size</th>
                <th className="px-4 py-3 font-semibold">Files</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map((row) => (
                <React.Fragment key={row.id}>
                  <tr
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => setExpandedId(expandedId === row.id ? null : row.id)}
                    data-testid={`quote-row-${row.id}`}
                  >
                    <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{formatWhen(row.created_at)}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">
                        {[row.first_name, row.last_name].filter(Boolean).join(' ') || <span className="text-gray-400">(no name)</span>}
                      </div>
                      <div className="text-xs flex items-center gap-1.5 text-[#4A7C59]">
                        <a
                          href={`mailto:${row.email}`}
                          onClick={(e) => e.stopPropagation()}
                          className="hover:underline flex items-center gap-1"
                        >
                          <Mail className="w-3 h-3" /> {row.email}
                        </a>
                        <EmailStatusChip row={row} />
                      </div>
                      {row.phone && (
                        <div className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                          <Phone className="w-3 h-3" /> {row.phone}
                          {row.support_call && <span className="ml-1 px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded text-[10px] font-semibold">call requested</span>}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-gray-900 font-medium line-clamp-1">{row.product || '—'}</div>
                      {(row.category || row.product_type) && (
                        <div className="text-xs text-gray-500 line-clamp-1">
                          {[row.category, row.product_type].filter(Boolean).join(' · ')}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                      {row.total_quantity ? `Qty ${row.total_quantity}` : '—'}
                      {(row.width_in || row.height_in) && (
                        <div className="text-xs text-gray-500">
                          {row.width_in || '?'}" × {row.height_in || '?'}"
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {row.attachment_count > 0 ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium">
                          <Paperclip className="w-3 h-3" /> {row.attachment_count}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-1 text-xs font-semibold rounded-full uppercase tracking-wide ${STATUS_BADGE[row.status] || 'bg-gray-100 text-gray-700'}`}>
                        {row.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleStatus(row); }}
                        disabled={mutatingId === row.id}
                        data-testid={`quote-toggle-${row.id}`}
                        className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                          row.status === 'quoted'
                            ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            : 'bg-[#4A7C59] text-white hover:bg-[#3D6A4A]'
                        }`}
                      >
                        {mutatingId === row.id ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : row.status === 'quoted' ? (
                          <><RotateCcw className="w-3 h-3" /> Mark as new</>
                        ) : (
                          <><Check className="w-3 h-3" /> Mark as quoted</>
                        )}
                      </button>
                    </td>
                  </tr>
                  {expandedId === row.id && (
                    <tr className="bg-gray-50/60" data-testid={`quote-details-${row.id}`}>
                      <td colSpan={7} className="px-6 py-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                          <div>
                            <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-1">Location / Use</div>
                            <div className="text-gray-800">{row.location || '—'}</div>
                            <div className="text-gray-600">{row.use_type || '—'}</div>
                          </div>
                          <div>
                            <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-1">Application use</div>
                            <div className="text-gray-800">{row.application_use || '—'}</div>
                          </div>
                          <div>
                            <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-1">Quoted by</div>
                            <div className="text-gray-800">{row.quoted_by || '—'}</div>
                            <div className="text-xs text-gray-500">{row.quoted_at ? formatWhen(row.quoted_at) : ''}</div>
                          </div>
                          <div className="md:col-span-3">
                            <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-1">Additional instructions</div>
                            <div className="text-gray-800 whitespace-pre-wrap">{row.additional_instructions || '—'}</div>
                          </div>
                          {row.attachment_count > 0 && (
                            <div className="md:col-span-3">
                              <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-2">Attachments</div>
                              <div className="flex flex-wrap gap-2">
                                {(row.attachment_names || []).map((name, idx) => (
                                  <button
                                    key={`${row.id}-${idx}-${name || idx}`}
                                    onClick={() => downloadAttachment(row.id, idx, name)}
                                    className="inline-flex items-center gap-2 px-3 py-1.5 border border-gray-300 rounded-md text-xs font-medium text-gray-700 hover:bg-white hover:border-[#4A7C59]"
                                    data-testid={`quote-attachment-${row.id}-${idx}`}
                                  >
                                    <FileDown className="w-3 h-3" />
                                    {name || `file-${idx + 1}`}
                                  </button>
                                ))}
                              </div>
                              <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                                <MailWarning className="w-3 h-3" />
                                Attachments are stored in /tmp and may be purged if the server restarts. Download soon if you need them.
                              </p>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default QuoteRequestsTable;
